/** Pure integrators behind the pull-to-refresh indicator. DOM-free on purpose: the
 * two things that can visibly go wrong — a ring that rocks backwards, a spring that gets flung by
 * a dropped frame — are both provable here without a browser. */

import { PULL_TRIGGER_DISTANCE } from './pullGesture'

/** Frame delta ceiling. A backgrounded tab hands back a delta of hundreds of ms; integrating it
 * whole would teleport the spring and skip most of a turn of the ring. */
export const MAX_FRAME_MS = 32

// ---------------------------------------------------------------------------------------------
// Rotation
// ---------------------------------------------------------------------------------------------

/** Steady spin while refreshing, in degrees per ms — 720ms a turn. */
export const SPIN_TARGET_OMEGA = 0.5

/** Time constants for reaching the target angular velocity, spinning up and winding down. */
export const SPIN_UP_TAU = 90
export const SPIN_DOWN_TAU = 120

/** Below this the ring is standing still; the frame loop can stop. */
export const SPIN_REST_OMEGA = 0.0005

/** Ceiling on the angular velocity handed over from the finger at release. */
export const SPIN_RELEASE_MAX_OMEGA = 1.2

/** How far the ring turns over one trigger-distance of pull. */
export const PULL_ANGLE_SWEEP = 200

export interface AngleState {
  /** Degrees. Only ever increases. */
  angle: number
  /** Degrees per ms. Never negative. */
  omega: number
}

/**
 * One frame of rotation. The velocity is eased toward `target`; the angle is never eased, only
 * accumulated — which is the whole point.
 *
 * Easing the angle instead (the obvious `base + elapsed * rate * fade` shape) makes the angle
 * shrink the moment `fade` falls faster than `elapsed` grows, and the ring visibly rocks back.
 * Here `omega` is a convex blend of two non-negative numbers, so it cannot cross zero, so the
 * angle cannot reverse.
 */
export function stepAngle(
  state: AngleState,
  dt: number,
  target: number,
  tau: number
): AngleState {
  const step = clampFrame(dt)
  const blend = 1 - Math.exp(-step / tau)
  const omega = state.omega + (target - state.omega) * blend
  return { angle: state.angle + omega * step, omega }
}

/** Angle the finger itself has wound onto the ring at this pulled distance. Unrounded: rounding
 * to whole degrees is what made a slow pull step instead of glide. */
export function pullAngle(distance: number): number {
  return (distance / PULL_TRIGGER_DISTANCE) * PULL_ANGLE_SWEEP
}

/** Angular velocity the finger was turning the ring at, from two successive pull samples. */
export function releaseOmega(
  previous: { angle: number; time: number },
  current: { angle: number; time: number }
): number {
  const dt = current.time - previous.time
  if (!(dt > 0)) return 0
  const omega = (current.angle - previous.angle) / dt
  if (!Number.isFinite(omega) || omega <= 0) return 0
  return Math.min(omega, SPIN_RELEASE_MAX_OMEGA)
}

// ---------------------------------------------------------------------------------------------
// Springs
// ---------------------------------------------------------------------------------------------

export interface SpringConfig {
  stiffness: number
  damping: number
}

/** Release to the resting offset: ζ ≈ 0.9, one soft settle, so the indicator lands rather than
 * arrives. */
export const SPRING_RELEASE: SpringConfig = { stiffness: 520, damping: 41 }

/** Settle back to zero on both the committed and the released-short paths: critically damped, no
 * overshoot — nothing should bounce on the way out. */
export const SPRING_SETTLE: SpringConfig = { stiffness: 700, damping: 52 }

const SPRING_SUBSTEP_MS = 4
const SPRING_VALUE_EPSILON = 0.1
const SPRING_VELOCITY_EPSILON = 1

export interface SpringState {
  value: number
  /** px per second. */
  velocity: number
  /** At the target and stopped. The caller may drop the frame loop. */
  done: boolean
}

export function springAt(value: number): SpringState {
  return { value, velocity: 0, done: true }
}

/**
 * One frame of a damped spring, sub-stepped so a long frame stays stable. Once it is within a
 * tenth of a pixel of the target it snaps to *exactly* the target and reports done — a spring
 * resting at 0.4px would leave a real transform on the content element, which is the modal trap.
 * 
 */
export function stepSpring(
  state: SpringState,
  target: number,
  config: SpringConfig,
  dt: number
): SpringState {
  let value = state.value
  let velocity = state.velocity
  let remaining = clampFrame(dt)

  while (remaining > 0) {
    const step = Math.min(remaining, SPRING_SUBSTEP_MS)
    remaining -= step
    const seconds = step / 1000
    const accel = -config.stiffness * (value - target) - config.damping * velocity
    velocity += accel * seconds
    value += velocity * seconds
  }

  if (
    Math.abs(value - target) < SPRING_VALUE_EPSILON &&
    Math.abs(velocity) < SPRING_VELOCITY_EPSILON
  ) {
    return { value: target, velocity: 0, done: true }
  }
  return { value, velocity, done: false }
}

function clampFrame(dt: number): number {
  if (!Number.isFinite(dt) || dt <= 0) return 0
  return Math.min(dt, MAX_FRAME_MS)
}
